import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BlogPost } from './types';
import { useLocaleContext } from '../context/LocaleContext';
import { getLocalizedSlug } from './slugMapping';
import { ArrowRight, Calendar } from 'lucide-react';
import { BlogImage } from '../components/BlogImage';

interface BlogPreviewCardProps {
  post: BlogPost;
}

export const BlogPreviewCard: React.FC<BlogPreviewCardProps> = ({ post }) => {
  const { locale } = useLocaleContext();
  const [localizedSlug, setLocalizedSlug] = useState<string>(post.slug);

  // Load localized slug
  useEffect(() => {
    getLocalizedSlug(post.slug, locale).then(slug => {
      setLocalizedSlug(slug);
    });
  }, [post.slug, locale]);

  // Debug logging
  console.log('BlogPreviewCard rendering post:', {
    slug: post.slug,
    localizedSlug,
    title: post.title,
    coverImage: post.coverImage
  });

  // Format date based on locale
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const localeMap: Record<string, string> = {
      'en': 'en-US',
      'de': 'de-DE',
      'pt-BR': 'pt-BR',
      'ms-MY': 'ms-MY',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'ja': 'ja-JP',
      'id': 'id-ID',
    };
    const browserLocale = localeMap[locale] || 'en-US';
    return date.toLocaleDateString(browserLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formattedDate = formatDate(post.date);

  // Get short excerpt (first 160 characters of description)
  const excerpt = post.description.length > 160
    ? post.description.substring(0, 160) + '...'
    : post.description;

  // Use localized slug for blog URL
  const getLocalePrefix = () => {
    if (locale === 'en') return '';
    if (locale === 'pt-BR') return '/pt-br';
    if (locale === 'ms-MY') return '/my';
    return `/${locale}`;
  };
  const localePrefix = getLocalePrefix();
  const blogUrl = `${localePrefix}/blog/${localizedSlug}`;

  return (
    <article className="group bg-white dark:bg-charcoal-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500">
      {/* Cover Image */}
      <div className="relative overflow-hidden aspect-video">
        <BlogImage
          coverImage={post.coverImage}
          title={post.title}
          variant="preview"
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>

      {/* Card Content */}
      <div className="p-6">
        {/* Date */}
        <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400 mb-3">
          <Calendar className="w-4 h-4" aria-hidden="true" />
          <time dateTime={post.date}>{formattedDate}</time>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-charcoal-900 dark:text-white mb-3 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors">
          {post.title}
        </h2>

        {/* Excerpt */}
        <p className="text-neutral-600 dark:text-neutral-300 mb-4 line-clamp-3">
          {excerpt}
        </p>

        {/* Keywords */}
        {post.keywords && post.keywords.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {post.keywords.slice(0, 3).map((keyword, index) => (
              <span
                key={index}
                className="px-3 py-1 text-xs font-medium bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 rounded-full"
              >
                {keyword}
              </span>
            ))}
          </div>
        )}

        {/* Read More Link */}
        <Link
          to={blogUrl}
          aria-label={`Read more: ${post.title}`}
          className="inline-flex items-center gap-2 text-accent-600 dark:text-accent-400 font-semibold hover:gap-3 transition-all group"
        >
          {locale === 'de'
            ? 'Mehr lesen'
            : locale === 'pt-BR'
            ? 'Leia mais'
            : locale === 'ms-MY'
            ? 'Baca lebih lanjut'
            : locale === 'es'
            ? 'Leer más'
            : locale === 'fr'
            ? 'Lire la suite'
            : locale === 'ja'
            ? '続きを読む'
            : locale === 'id'
            ? 'Baca selengkapnya'
            : 'Read more'}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
};
